import type {MonthColumn} from "../../utils";
import {isPeriodicCheckImbalanced, rowLabel} from "../../utils";
import {ReportRowKind, type PeriodicBalanceSheetViewRow} from "../../types";
import {SignedMoneyCell} from "../StatementTable/AmountCells.tsx";
import {AccountLabelCell} from "../PeriodicStatementTable/AccountLabelCell.tsx";
import {COL_SEP_L, rowClassName} from "../PeriodicStatementTable/tableStyles.ts";
import type {LegalEntityType} from "../../../../../generated/enums.ts";

interface PeriodicBalanceSheetTableRowProps {
    row: PeriodicBalanceSheetViewRow;
    columns: MonthColumn[];
    currency: string;
    legalEntityType: LegalEntityType;
    onToggleNode: (id: string) => void;
    onCellClick: (row: PeriodicBalanceSheetViewRow, periodIndex: number) => void;
}

export function PeriodicBalanceSheetTableRow({
    row,
    columns,
    currency,
    legalEntityType,
    onToggleNode,
    onCellClick,
}: PeriodicBalanceSheetTableRowProps) {
    const expandable = row.kind === ReportRowKind.Group;
    const drillable = row.kind === ReportRowKind.Account && row.lines.length > 0;
    const imbalanced = isPeriodicCheckImbalanced(row);
    const lastIndex = columns.length - 1;

    function onRowActivate() {
        if (expandable) onToggleNode(row.id);
        else if (drillable && lastIndex >= 0) onCellClick(row, lastIndex);
    }

    return (
        <tr
            className={`group ${rowClassName(row, imbalanced)} ${expandable || drillable ? "cursor-pointer" : ""}`}
            onClick={onRowActivate}
        >
            <AccountLabelCell
                row={row}
                label={rowLabel(row, legalEntityType)}
                currency={currency}
                expandable={expandable}
                expanded={row.expanded}
                drillable={drillable}
                isImbalanced={imbalanced}
            />
            <td
                className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${imbalanced ? "" : "bg-[#F4FAFE]"}`}
                onClick={(event) => event.stopPropagation()}
            >
                <SignedMoneyCell money={row.opening_balance} />
            </td>
            {columns.map((column, index) => (
                <td
                    key={column.label}
                    className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${COL_SEP_L} ${
                        drillable ? "cursor-pointer hover:bg-[#EAF5FD]" : ""
                    }`}
                    onClick={
                        drillable
                            ? (event) => {
                                  event.stopPropagation();
                                  onCellClick(row, index);
                              }
                            : undefined
                    }
                >
                    {row.balances[index] && <SignedMoneyCell money={row.balances[index]} />}
                </td>
            ))}
        </tr>
    );
}
